import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Search,
  Filter,
  PlusCircle,
  Eye,
  Edit,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  User,
  Phone,
  Calendar,
  Building2,
  RefreshCw,
  TrendingUp,
  Download,
} from "lucide-react";
import { DatePicker, Modal, Input, InputNumber, message } from "antd";
import dayjs from "dayjs";
import "antd/dist/reset.css";
import { useTheme } from "../context/ThemeContext";
import { usePermissions } from "../context/PermissionContext";
import { fintechApi } from "../services/api";

const { RangePicker } = DatePicker;

// Status styling map
const STATUS_CONFIG = {
  DRAFT: { label: "Draft", bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-200" },
  SUBMITTED: { label: "Submitted", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  UNDER_REVIEW: { label: "Under Review", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  APPROVED: { label: "Approved / Sanctioned", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
  REJECTED: { label: "Rejected", bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  DISBURSED: { label: "Disbursed", bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200" },
  CLOSED: { label: "Closed", bg: "bg-slate-100", text: "text-slate-800", border: "border-slate-300" },
};

const formatINR = (val) => {
  if (val === undefined || val === null || isNaN(val)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

// Blob ko file download me badalne ka chhota helper — sirf yahin ek jagah
const triggerBlobDownload = (blob, fileName) => {
  if (!blob || typeof blob.size !== "number" || blob.size === 0) {
    throw new Error("Server ne khali file bheji hai");
  }
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default function LoanApplicationList() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const primaryColor = currentTheme?.color || "#2e856e";
  const { has } = usePermissions();
  const [searchParams] = useSearchParams();

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filters State — dashboard cards `?status=` bhejte hain
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") || "ALL");
  const [loanTypeFilter, setLoanTypeFilter] = useState("ALL");
  const [dateRange, setDateRange] = useState(null);

  // Quick Action Modals
  const [approveModal, setApproveModal] = useState({ open: false, loan: null, sanctionedAmount: 0, interestRate: 12.0, tenure: 24 });
  const [rejectModal, setRejectModal] = useState({ open: false, loan: null, reason: "" });

  // Har keystroke pe API call na ho
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchLoans = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "ALL") params.status = statusFilter;
      if (loanTypeFilter !== "ALL") params.loanType = loanTypeFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format("YYYY-MM-DD");
        params.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      const res = await fintechApi.getAllLoans(params);
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.content)
        ? res.content
        : Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.loans)
        ? res.loans
        : [];
      setApplications(list);
    } catch (err) {
      console.error("Error fetching loans:", err);
      message.error(err.message || "Failed to load loan applications");
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, loanTypeFilter, debouncedSearch, dateRange]);

  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);

  // Safety net — backend koi filter ignore kare to bhi list sahi dikhe
  const filteredLoans = applications.filter((loan) => {
    const matchesSearch =
      searchTerm === "" ||
      loan.applicantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      loan.phone?.includes(searchTerm) ||
      loan.applicationId?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "ALL" || loan.status === statusFilter;
    const matchesLoanType = loanTypeFilter === "ALL" || loan.loanType === loanTypeFilter;

    return matchesSearch && matchesStatus && matchesLoanType;
  });

  // Handle Approve — API fail ho to status nahi badlega, modal khula rahega
  const handleConfirmApprove = async () => {
    if (!approveModal.loan) return;
    if (!(Number(approveModal.sanctionedAmount) > 0)) {
      message.warning("Please enter a valid sanctioned amount");
      return;
    }
    setActionLoading(true);
    try {
      await fintechApi.approveLoan(approveModal.loan.id, {
        sanctionedAmount: approveModal.sanctionedAmount,
        interestRate: approveModal.interestRate,
        tenure: approveModal.tenure,
      });
      message.success(`Loan Application ${approveModal.loan.applicationId} approved successfully!`);
      setApproveModal({ open: false, loan: null, sanctionedAmount: 0, interestRate: 12.0, tenure: 24 });
      fetchLoans();
    } catch (err) {
      console.error("Approve loan failed:", err);
      message.error(err.message || "Failed to approve loan");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Reject
  const handleConfirmReject = async () => {
    if (!rejectModal.loan || !rejectModal.reason.trim()) {
      message.warning("Please provide a reason for rejection");
      return;
    }
    setActionLoading(true);
    try {
      await fintechApi.rejectLoan(rejectModal.loan.id, rejectModal.reason.trim());
      message.success(`Loan Application ${rejectModal.loan.applicationId} marked as Rejected.`);
      setRejectModal({ open: false, loan: null, reason: "" });
      fetchLoans();
    } catch (err) {
      console.error("Reject loan failed:", err);
      message.error(err.message || "Failed to reject loan");
    } finally {
      setActionLoading(false);
    }
  };

  // 📥 Excel Export — wahi filters bhejo jo abhi list par lage hain
  // Note: is page par employee/assignedTo ka koi filter abhi hai nahi,
  // isliye woh param nahi bheja ja raha. Filter add ho to yahan bhi add karna.
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== "ALL") params.status = statusFilter;
      if (loanTypeFilter && loanTypeFilter !== "ALL") params.loanType = loanTypeFilter;

      const term = (debouncedSearch || searchTerm || "").trim();
      if (term) params.search = term;

      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format("YYYY-MM-DD");
        params.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      // Blob aane ke baad hi success dikhana hai — fail hua to catch me jayega
      const blob = await fintechApi.exportLoanApplications(params);
      triggerBlobDownload(blob, `loan-applications-${dayjs().format("DD-MM-YYYY")}.xlsx`);
      message.success("Loan applications exported successfully");
    } catch (err) {
      console.error("Export loan applications failed:", err);
      message.error(err?.message || "Failed to export loan applications");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* ── Page Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md"
            style={{ backgroundColor: primaryColor }}
          >
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              Loan Applications
            </h1>
            <p className="text-xs md:text-sm text-gray-500">
              Manage applicant requests, verify documents, and sanction loans
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchLoans}
            disabled={loading}
            className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl border border-gray-200 transition-all shadow-sm"
            title="Refresh List"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>

          {/* Excel Export — current filters ke saath */}
          <button
            onClick={handleExportExcel}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl border border-[#2e856e]/30 bg-[#2e856e]/5 text-[#2e856e] shadow-sm transition-all hover:bg-[#2e856e]/10 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            title="Download applied filters as Excel"
          >
            {exporting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span>{exporting ? "Exporting..." : "Export Excel"}</span>
          </button>

          {has("LOAN_CREATE") && (
            <button
              onClick={() => navigate("/fintech/loans/new")}
              className="flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow-md transition-all hover:brightness-105 active:scale-95"
              style={{ backgroundColor: primaryColor }}
            >
              <PlusCircle className="w-4 h-4" />
              <span>New Application</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div className="bg-white p-4 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {/* Search Box */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search Name, Phone, App ID..."
              className="w-full pl-10 pr-4 py-2 text-xs md:text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e]"
            />
          </div>

          {/* Status Dropdown */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 text-xs md:text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e] font-medium text-gray-700"
            >
              <option value="ALL">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="SUBMITTED">Submitted</option>
              <option value="UNDER_REVIEW">Under Review</option>
              <option value="APPROVED">Approved / Sanctioned</option>
              <option value="DISBURSED">Disbursed</option>
              <option value="REJECTED">Rejected</option>
              <option value="CLOSED">Closed</option>
            </select>
          </div>

          {/* Loan Type Dropdown */}
          <div>
            <select
              value={loanTypeFilter}
              onChange={(e) => setLoanTypeFilter(e.target.value)}
              className="w-full px-3 py-2 text-xs md:text-sm bg-gray-50 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2e856e]/20 focus:border-[#2e856e] font-medium text-gray-700"
            >
              <option value="ALL">All Loan Types</option>
              <option value="PERSONAL">Personal Loan</option>
              <option value="BUSINESS">Business Loan</option>
              <option value="HOME">Home Loan</option>
              <option value="VEHICLE">Vehicle Loan</option>
              <option value="EDUCATION">Education Loan</option>
              <option value="GOLD">Gold Loan</option>
              <option value="BNPL">BNPL</option>
              <option value="MSME">MSME</option>
            </select>
          </div>

          {/* Date Range Picker */}
          <div>
            <RangePicker
              value={dateRange}
              onChange={(val) => setDateRange(val)}
              format="DD/MM/YYYY"
              placeholder={["Start Date", "End Date"]}
              className="w-full rounded-xl border-gray-200 py-1.5 shadow-none"
            />
          </div>

          {/* Quick Clear Button */}
          <div className="flex items-center">
            <button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("ALL");
                setLoanTypeFilter("ALL");
                setDateRange(null);
              }}
              className="w-full px-3 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      {/* ── Loan Applications Table ── */}
      <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border-collapse">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/75 text-gray-600 text-xs uppercase font-bold">
                <th className="py-4 px-4">Application ID</th>
                <th className="py-4 px-4">Applicant</th>
                <th className="py-4 px-4">Loan Type</th>
                <th className="py-4 px-4 text-right">Requested Amount</th>
                <th className="py-4 px-4 text-center">Status</th>
                <th className="py-4 px-4">Assigned To</th>
                <th className="py-4 px-4">Created Date</th>
                <th className="py-4 px-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredLoans.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-gray-400">
                    <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="font-semibold">No loan applications found</p>
                    <p className="text-xs text-gray-400 mt-1">Try changing filters or search terms</p>
                  </td>
                </tr>
              ) : (
                filteredLoans.map((loan) => {
                  const statusInfo = STATUS_CONFIG[loan.status] || STATUS_CONFIG.DRAFT;
                  return (
                    <tr
                      key={loan.id}
                      className="hover:bg-gray-50/60 transition-colors group cursor-pointer"
                      onClick={() => navigate(`/fintech/loans/${loan.id}`)}
                    >
                      {/* App ID */}
                      <td className="py-3.5 px-4 font-bold text-gray-900">
                        <span className="font-mono text-xs px-2 py-1 bg-gray-100 rounded-lg group-hover:bg-[#2e856e]/10 group-hover:text-[#2e856e] transition-colors">
                          {loan.applicationId}
                        </span>
                      </td>

                      {/* Applicant Name & Phone */}
                      <td className="py-3.5 px-4">
                        <div className="font-semibold text-gray-900">{loan.applicantName}</div>
                        <div className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                          <Phone className="w-3 h-3" />
                          <span>{loan.phone}</span>
                        </div>
                      </td>

                      {/* Loan Type */}
                      <td className="py-3.5 px-4">
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-gray-100 text-gray-700">
                          {loan.loanType}
                        </span>
                      </td>

                      {/* Amount */}
                      <td className="py-3.5 px-4 text-right font-black text-gray-900">
                        {formatINR(loan.amount)}
                      </td>

                      {/* Status Badge */}
                      <td className="py-3.5 px-4 text-center">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${statusInfo.bg} ${statusInfo.text} ${statusInfo.border}`}
                        >
                          {statusInfo.label}
                        </span>
                      </td>

                      {/* Assigned To */}
                      <td className="py-3.5 px-4 text-xs font-medium text-gray-600">
                        {loan.assignedTo || "Unassigned"}
                      </td>

                      {/* Created Date */}
                      <td className="py-3.5 px-4 text-xs text-gray-500 whitespace-nowrap">
                        {dayjs(loan.createdAt).format("DD/MM/YYYY hh:mm A")}
                      </td>

                      {/* Actions */}
                      <td
                        className="py-3.5 px-4 text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-center gap-1.5">
                          <button
                            onClick={() => navigate(`/fintech/loans/${loan.id}`)}
                            className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {["DRAFT", "SUBMITTED"].includes(loan.status) && has("LOAN_EDIT") && (
                            <button
                              onClick={() => navigate(`/fintech/loans/${loan.id}/edit`)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                              title="Edit Application"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          )}

                          {loan.status === "UNDER_REVIEW" && has("LOAN_APPROVE") && (
                            <>
                              <button
                                onClick={() =>
                                  setApproveModal({
                                    open: true,
                                    loan,
                                    sanctionedAmount: loan.amount,
                                    interestRate: loan.interestRate || 12.0,
                                    tenure: loan.tenure || 24,
                                  })
                                }
                                className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all"
                                title="Approve Loan"
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() =>
                                  setRejectModal({ open: true, loan, reason: "" })
                                }
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                title="Reject Loan"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}

                          {loan.status === "APPROVED" && has("LOAN_DISBURSE") && (
                            <button
                              onClick={() => navigate(`/fintech/loans/${loan.id}`)}
                              className="px-2 py-1 text-xs font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-all"
                            >
                              Disburse
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Quick Approve Modal ── */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-emerald-700 font-bold text-base">
            <CheckCircle2 className="w-5 h-5" />
            <span>Sanction & Approve Loan</span>
          </div>
        }
        open={approveModal.open}
        onOk={handleConfirmApprove}
        onCancel={() => setApproveModal({ open: false, loan: null, sanctionedAmount: 0, interestRate: 12.0, tenure: 24 })}
        okText="Confirm Approval"
        confirmLoading={actionLoading}
        okButtonProps={{ style: { backgroundColor: "#10b981", borderColor: "#10b981" } }}
      >
        {approveModal.loan && (
          <div className="space-y-4 py-3 text-sm">
            <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-xs">
              <div className="font-bold text-gray-800">{approveModal.loan.applicantName}</div>
              <div className="text-gray-500">App ID: {approveModal.loan.applicationId} | Type: {approveModal.loan.loanType}</div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Sanctioned Amount (₹)
              </label>
              <InputNumber
                className="w-full rounded-xl"
                value={approveModal.sanctionedAmount}
                onChange={(val) => setApproveModal((prev) => ({ ...prev, sanctionedAmount: val }))}
                formatter={(val) => `₹ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(val) => val.replace(/₹\s?|(,*)/g, "")}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Interest Rate (% p.a.)
                </label>
                <InputNumber
                  className="w-full rounded-xl"
                  min={1}
                  max={40}
                  step={0.25}
                  value={approveModal.interestRate}
                  onChange={(val) => setApproveModal((prev) => ({ ...prev, interestRate: val }))}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Tenure (Months)
                </label>
                <InputNumber
                  className="w-full rounded-xl"
                  min={3}
                  max={360}
                  value={approveModal.tenure}
                  onChange={(val) => setApproveModal((prev) => ({ ...prev, tenure: val }))}
                />
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Quick Reject Modal ── */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-red-600 font-bold text-base">
            <XCircle className="w-5 h-5" />
            <span>Reject Loan Application</span>
          </div>
        }
        open={rejectModal.open}
        onOk={handleConfirmReject}
        onCancel={() => setRejectModal({ open: false, loan: null, reason: "" })}
        okText="Reject File"
        confirmLoading={actionLoading}
        okButtonProps={{ danger: true }}
      >
        {rejectModal.loan && (
          <div className="space-y-3 py-2 text-sm">
            <p className="text-gray-600 text-xs">
              Are you sure you want to reject <strong>{rejectModal.loan.applicantName}</strong> ({rejectModal.loan.applicationId})?
            </p>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">
                Rejection Reason *
              </label>
              <Input.TextArea
                rows={3}
                placeholder="e.g. Low CIBIL score (580), irregular bank cash flow, incomplete ITR..."
                value={rejectModal.reason}
                onChange={(e) => setRejectModal((prev) => ({ ...prev, reason: e.target.value }))}
                className="rounded-xl"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
